import React, { useCallback, useEffect, useState } from 'react'
import { styled, useMediaQuery } from '@mui/material'
import { keyframes } from '@mui/system'
import { WindowContainer } from './WindowContainer'

const Container = styled(WindowContainer)(({ theme }) => ({
    zIndex: 3,
}))

// Pulse while the scan button is enabled: subtle button breathing plus an
// expanding/fading ring behind it (ref: https://codepen.io/seansean11/pen/DgGYPY).
const pulseRing = keyframes`
    0% {
        transform: scale(1);
        opacity: 0.5;
    }
    100% {
        transform: scale(2);
        opacity: 0;
    }
`

const pulseButton = keyframes`
    0% {
        transform: scale(0.9);
    }
    70% {
        transform: scale(1);
    }
    100% {
        transform: scale(0.9);
    }
`

const StartButton = styled('button')(({ theme }) => ({
    position: 'absolute',
    bottom: '20px',
    left: 'calc(50vw - 3.6rem)',
    width: '7.2rem',
    height: '7.2rem',
    background: 'transparent',
    border: 'none',
    zIndex: 5,
    '&:hover': {
        cursor: 'pointer',
    },
    '> img': {
        width: '100%',
        filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.6))',
    },
    '&:active > img': {
        opacity: 0.8,
    },
    '&:not(:disabled)': {
        animation: `${pulseButton} 1.5s ease-out infinite`,
    },
    '&:not(:disabled)::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: '50%',
        backgroundColor: 'rgb(51, 217, 51)',
        animation: `${pulseRing} 1.5s ease-out infinite`,
        zIndex: -1,
    },
}))

const Scanner = styled('div')(({ theme }) => ({
    margin: 'auto',
    height: '4px',
    backgroundColor: '#66ff00',
    opacity: 0.8,
    position: 'relative',
    zIndex: 3,
}))

const ScanLabel = styled('p')(({ theme }) => ({
    margin: 0,
    padding: '0 15%',
    fontFamily: 'Montserrat, -apple-system, BlinkMacSystemFont, sans-serif',
    fontSize: '30px',
    fontWeight: 700,
    lineHeight: 1.3,
    textAlign: 'center',
    color: 'white',
    textShadow: '0 0 5px #000',
    whiteSpace: 'pre-line',
}))

const portrait = {
    w: 3,
    h: 4.2,
}

function Scanning({
    ready,
    hidden,
    button,
    showScannerEffect = false,
    scanning,
    scanLabel,
    onScanning,
}) {
    const isSm = useMediaQuery((theme) => theme.breakpoints.down('sm'))

    const [loaded, setLoaded] = useState(false)
    const [top, setTop] = useState(0)
    const [width, setWidth] = useState(0)
    const [scannerHeight, setScannerHeight] = useState(0)
    const [scannerWidth, setScannerWidth] = useState(0)
    const [kframe, setKframe] = useState()

    const updateViewport = useCallback(
        (event) => {
            const maxWidth = isSm ? 400 : 520
            const margin = 64

            let w, h
            if (maxWidth + margin < window.innerWidth) {
                w = maxWidth
            } else {
                w = window.innerWidth - margin
            }

            h = Math.round((w * portrait.h) / portrait.w)

            const minTop = 40
            const buttonArea = 158
            const usableHeight = window.innerHeight - buttonArea - minTop
            const top = Math.max(minTop, minTop + Math.floor((usableHeight - h) / 2)) + 8

            const width = (window.innerWidth - w) / 2
            const bottom = top + h

            const kframe = keyframes`
            0% {
                top: ${top}px;
                opacity: 1;
            }
            50% {
                top: ${bottom - 4}px;
                opacity: 1;
            }
            90% {
                opacity: 1;
            }
            100% {
                top: ${top}px;
                opacity: 0;
            }
        `
            setTop(top)
            setWidth(width)
            setScannerHeight(h)
            setScannerWidth(w)
            setKframe(kframe)
            setLoaded(true)
        },
        [isSm]
    )

    useEffect(() => {
        window.addEventListener('resize', updateViewport)

        updateViewport()

        return () => {
            window.removeEventListener('resize', updateViewport)
        }
    }, [updateViewport])

    if (!loaded) {
        return <></>
    }

    return (
        <>
            <Container
                id="scanning-overlay"
                sx={hidden ? { visibility: 'hidden' } : undefined}
            >
                {showScannerEffect && scanning && (
                    <Scanner
                        sx={{
                            width: `${scannerWidth}px`,
                            animation: `${kframe} 2s linear forwards`,
                        }}
                    />
                )}
                {!button && !scanning && (
                    <div style={{
                        position: 'absolute',
                        top: `${top}px`,
                        right: `${width}px`,
                        left: `${width}px`,
                        height: `${scannerHeight}px`,
                        backgroundImage: "url('/Scan-Brackets.svg')",
                        backgroundPosition: "center center",
                        backgroundSize: "100% auto",
                        backgroundRepeat: "no-repeat",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}>
                        <ScanLabel>{scanLabel}</ScanLabel>
                    </div>
                )}
            </Container>

            {ready === 'yes' && (
                <StartButton onClick={onScanning} disabled={scanning}>
                    <img
                        src={scanning ? '/Scan-Button-Disabled.png' : '/Scan-Button.png'}
                        alt="start"
                    />
                </StartButton>
            )}
        </>
    )
}

export { Scanning }
